import React from 'react';
import styled from 'styled-components';
import { FiHeadphones, FiDollarSign, FiSmartphone, FiZap, FiUsers, FiShield } from 'react-icons/fi';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.12, ease: 'easeOut' }
  })
};

const Section = styled.section`
  width: 100%;
  background: #fff;
  padding: 100px 20px 80px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  direction: rtl;
`;

const Eyebrow = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #DA015C;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 900;
  margin-bottom: 20px;
  color: #0a174e;
  letter-spacing: -0.02em;
  text-align: center;
  line-height: 1.1;
  span { color: #DA015C; }
`;

const Description = styled.p`
  font-size: 1.12rem;
  color: #5a6a7c;
  margin-bottom: 64px;
  max-width: 580px;
  text-align: center;
  line-height: 1.9;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  max-width: 1100px;
  width: 100%;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; gap: 16px; }
`;

const Card = styled(motion.div)`
  background: #f8fafb;
  border-radius: 20px;
  padding: 36px 28px;
  border: 1px solid rgba(10,23,78,0.05);
  position: relative;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  text-align: right;
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 40px rgba(10,23,78,0.10);
    border-color: rgba(218,1,92,0.15);
  }
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #DA015C, #0a174e);
    opacity: 0;
    transition: opacity 0.25s ease;
  }
  &:hover::before { opacity: 1; }
`;

const IconWrap = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #0a174e 0%, #13245c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  svg { color: #fff; font-size: 1.4rem; }
`;

const CardTitle = styled.h3`
  font-size: 1.12rem;
  font-weight: 800;
  margin-bottom: 10px;
  color: #0a174e;
`;

const CardDesc = styled.p`
  font-size: 0.95rem;
  color: #5a6a7c;
  line-height: 1.75;
`;

const features = [
  {
    icon: <FiShield />,
    title: 'الأمان أولاً',
    desc: 'جميع السائقين يخضعون لفحص أمني شامل. تتبع GPS لكل رحلة يبقيك أنت وعائلتك بأمان.'
  },
  {
    icon: <FiDollarSign />,
    title: 'أسعار شفافة',
    desc: 'لا مفاجآت في التسعير. اعرف التعرفة الكاملة قبل تأكيد الرحلة — بدون رسوم خفية أبداً.'
  },
  {
    icon: <FiZap />,
    title: 'وصول سريع',
    desc: 'السيارة تصل في دقائق. نظام التوزيع الذكي يربطك بأقرب سائق فوراً.'
  },
  {
    icon: <FiSmartphone />,
    title: 'تجربة سلسة',
    desc: 'احجز وتتبع وادفع من تطبيق واحد مصمم بعناية. بسيط للجميع وقوي لكل الاحتياجات.'
  },
  {
    icon: <FiHeadphones />,
    title: 'دعم ٢٤/٧',
    desc: 'فريق الدعم متاح على مدار الساعة — ليلاً ونهاراً — للمساعدة متى احتجتها.'
  },
  {
    icon: <FiUsers />,
    title: 'لكل مدينة',
    desc: 'واصل مصمم للمجتمعات التي تستحق خدمة نقل أفضل. قادمون إلى مدن جديدة كل شهر.'
  }
];

const AboutSection: React.FC = () => (
  <Section id="about">
    <Eyebrow>لماذا واصل</Eyebrow>
    <Title>رحلات مبنية لـ<span>مجتمعات حقيقية</span></Title>
    <Description>
      واصل هو تطبيق التاكسي من الجيل الجديد الذي يجمع أحدث التقنيات مع الخبرة المحلية لتقديم رحلات آمنة وموثوقة وبأسعار مناسبة — أينما كنت.
    </Description>
    <Grid>
      {features.map((f, i) => (
        <Card
          key={i}
          custom={i}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <IconWrap>{f.icon}</IconWrap>
          <CardTitle>{f.title}</CardTitle>
          <CardDesc>{f.desc}</CardDesc>
        </Card>
      ))}
    </Grid>
  </Section>
);

export default AboutSection;
